import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "@/components/ui/resizable-navbar";
import { MobileMenuContent } from "@/components/MobileMenuContent";
import { useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import logoWhite from "@/assets/logo-white.svg";
import logoBlack from "@/assets/logo-black.svg";
import Footer from "@/components/Footer";
import { CtaFooter, DynamicCtaFooter, PageSeo } from "@/components/ds";
import investCtaBg from "@/assets/invest-cta-bg.jpg";
import InvestGallerySection from "@/components/InvestGallerySection";
import { useInvestmentCardsByCategory } from "@/hooks/useInvestmentCards";
import { useWaitlistModal } from "@/components/waitlist/WaitlistContext";
import SecondaryNav from "@/components/SecondaryNav";
import { useSectionHeader, parseHeaderText } from "@/hooks/useSectionHeaders";
import { usePrerenderReady } from "@/hooks/usePrerenderReady";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { SITE_URL, buildUrl } from "@/config/site";

const NavButtons = ({ visible, onWaitlistClick }: { visible?: boolean; onWaitlistClick: (triggerElement?: string) => void }) => (
  <div className="flex items-center gap-8">
    <NavbarButton href="/contact" variant="secondary" visible={visible}>Contact</NavbarButton>
    <NavbarButton variant="primary" visible={visible} onClick={() => onWaitlistClick('navbar_cta')}>Join Waitlist</NavbarButton>
  </div>
);

export default function Invest() {
  useScrollDepth();
  const navItems = [
    { name: "Invest", link: "/invest" },
    { name: "Learn", link: "/learn" },
    { name: "About", link: "/about" },
  ];

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSecondaryNav, setShowSecondaryNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { openModal } = useWaitlistModal();

  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const scrollDelta = latest - lastScrollY;
    
    // Always show when at the top of the page
    if (latest <= 50) {
      setShowSecondaryNav(true);
    }
    // Only trigger hide after scrolling down 50px+
    else if (scrollDelta > 10 && latest > 100) {
      setShowSecondaryNav(false);
    }
    // Only trigger show after scrolling up 30px+
    else if (scrollDelta < -30) {
      setShowSecondaryNav(true);
    }
    
    // Only update lastScrollY if we've moved significantly (reduces jitter)
    if (Math.abs(scrollDelta) > 5) {
      setLastScrollY(latest);
    }
  });

  const secondaryNavItems = [
    { label: "Portfolios", anchor: "portfolios" },
    { label: "Funds", anchor: "funds" },
    { label: "Deals", anchor: "deals" },
  ];

  // Fetch cards from database
  const { data: portfolios, isLoading: portfoliosLoading } = useInvestmentCardsByCategory('portfolio');
  const { data: funds, isLoading: fundsLoading } = useInvestmentCardsByCategory('fund');
  const { data: deals, isLoading: dealsLoading } = useInvestmentCardsByCategory('deal');

  // CMS section headers
  const portfoliosHeader = useSectionHeader('invest', 'portfolios');
  const fundsHeader = useSectionHeader('invest', 'funds');
  const dealsHeader = useSectionHeader('invest', 'deals');

  // Signal to Prerender.io when all content is loaded
  const isContentLoaded = !portfoliosLoading && !fundsLoading && !dealsLoading;
  usePrerenderReady(isContentLoaded);

  // Helper to extract title and subtitle from CMS header_text (splits on ||)
  const extractTitleSubtitle = (headerText: string) => {
    const parts = parseHeaderText(headerText);
    let title = '';
    let subtitle = '';
    let inSecondPart = false;
    
    for (const part of parts) {
      if (part.type === 'br') {
        inSecondPart = true;
        continue;
      }
      const text = part.content || '';
      if (!inSecondPart) {
        title += text;
      } else {
        subtitle += text;
      }
    }
    return { title: title.trim(), subtitle: subtitle.trim() };
  };

  // Transform database cards to gallery format
  const transformCards = (cards: typeof portfolios) => 
    cards.map(card => ({
      title: card.title,
      subtitle: card.subtitle,
      image: card.image_url,
      pills: card.pills.map(pill => ({
        icon: pill.icon,
        label: pill.label,
      })),
    }));

  return (
    <div className="relative w-full min-h-screen bg-background">
      <PageSeo pageSlug="/invest">
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": buildUrl('/invest'),
                "url": buildUrl('/invest'),
                "name": "Investment Portfolios, Funds & Deals | Fedgroup Private Markets",
                "description": "Explore professionally managed portfolios, diversified funds, and direct investment deals in private markets. Access institutional-quality alternatives with Fedgroup.",
                "isPartOf": { "@type": "WebSite", "@id": `${SITE_URL}/#website` }
              },
              ...(portfolios ? portfolios.map(card => ({
                "@type": "InvestmentFund",
                "name": card.title,
                ...(card.subtitle ? { "description": card.subtitle } : {}),
                "category": "Portfolio",
                "url": buildUrl('/invest#portfolios'),
                "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
              })) : []),
              ...(funds ? funds.map(card => ({
                "@type": "InvestmentFund",
                "name": card.title,
                ...(card.subtitle ? { "description": card.subtitle } : {}),
                "category": "Fund",
                "url": buildUrl('/invest#funds'),
                "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
              })) : []),
              ...(deals ? deals.map(card => ({
                "@type": "InvestmentFund",
                "name": card.title,
                ...(card.subtitle ? { "description": card.subtitle } : {}),
                "category": "Deal",
                "url": buildUrl('/invest#deals'),
                "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
              })) : []),
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": buildUrl('/') },
                  { "@type": "ListItem", "position": 2, "name": "Invest", "item": buildUrl('/invest') }
                ]
              }
            ]
          })}
        </script>
      </PageSeo>
      <Navbar forceVisible mobileMenuOpen={isMobileMenuOpen}>
        <NavBody>
          <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
          <NavItems items={navItems} />
          <NavButtons onWaitlistClick={openModal} />
        </NavBody>

        <MobileNav>
          <MobileNavHeader>
            <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </MobileNavHeader>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          >
            <MobileMenuContent
              navItems={navItems}
              onItemClick={() => setIsMobileMenuOpen(false)}
              onWaitlistClick={openModal}
              secondaryNavItems={secondaryNavItems}
            />
          </MobileNavMenu>
        </MobileNav>
      </Navbar>

      {/* Secondary Navigation */}
      <motion.div
        initial={{ y: 0 }}
        animate={{ y: showSecondaryNav ? 0 : -100 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-[44px] left-0 right-0 z-30 hidden lg:block"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "saturate(180%) blur(20px)",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex justify-center">
          <SecondaryNav items={secondaryNavItems} visible={true} />
        </div>
      </motion.div>

      {/* Spacer for fixed navbar + secondary nav */}
      <div className="h-[44px] lg:h-[92px]" />

      {/* SEO H1 - visually hidden but accessible */}
      <h1 className="sr-only">Explore Private Market Investments - Portfolios, Funds & Deals</h1>

      {/* Page Content */}
      <main>
        <InvestGallerySection
          id="portfolios"
          title={extractTitleSubtitle(portfoliosHeader?.header_text || 'Explore our||**portfolios**').title}
          subtitle={extractTitleSubtitle(portfoliosHeader?.header_text || 'Explore our||**portfolios**').subtitle}
          items={transformCards(portfolios)}
          description={portfoliosHeader?.sub_header || "Professionally managed combinations of funds and deals, designed to match your investment goals and risk appetite."}
          isLoading={portfoliosLoading}
        />

        {/* atom-surface-section-contrast */}
        <InvestGallerySection
          id="funds"
          title={extractTitleSubtitle(fundsHeader?.header_text || 'Discover our||**funds**').title}
          subtitle={extractTitleSubtitle(fundsHeader?.header_text || 'Discover our||**funds**').subtitle}
          items={transformCards(funds)}
          description={fundsHeader?.sub_header || "Pooled investment vehicles offering diversified exposure to specific asset classes and strategies."}
          className="bg-platinum"
          isLoading={fundsLoading}
        />

        {/* atom-surface-section-default */}
        <InvestGallerySection
          id="deals"
          title={extractTitleSubtitle(dealsHeader?.header_text || 'Browse available||**deals**').title}
          subtitle={extractTitleSubtitle(dealsHeader?.header_text || 'Browse available||**deals**').subtitle}
          items={transformCards(deals)}
          description={dealsHeader?.sub_header || "Direct investment opportunities in individual projects and assets across various sectors."}
          isLoading={dealsLoading}
        />
      </main>

      {/* component-cta-footer: Dynamic CMS or fallback */}
      <DynamicCtaFooter
        pageSlug="/invest"
        fallback={<CtaFooter backgroundImage={investCtaBg} />}
      />
      <Footer />
    </div>
  );
}
